import { useState, useEffect, useMemo, useRef } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { api } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { Input, Select, Spinner, TextArea } from '../../components/shared/StyledComponents'
import LoadHistoryChart from './components/LoadHistoryChart'
import { ImputacionLinkCard } from '../Imputacion/components/ImputacionLinkCard'
import { OpportunitySummaryModal } from '../Timeline/components/OpportunitySummaryModal'
import { openOpportunity } from '../../utils/openOpportunity'
import type { CvExtraction, CvExtractionState, Experience } from '../../types/cv'
import { MAX_EXPERIENCES, applyCvExtraction, describeAutofill, isYearMonth } from './cvAutofill'

// Check if we're inside MainLayout (has path starting with /)
const PageContainer = styled.div<{ $inLayout?: boolean }>`
  min-height: ${({ $inLayout }) => $inLayout ? 'auto' : '100vh'};
  background: ${({ theme }) => theme.colors.background};
  display: flex;
  align-items: ${({ $inLayout }) => $inLayout ? 'flex-start' : 'center'};
  justify-content: center;
  padding: ${({ theme, $inLayout }) => $inLayout ? theme.spacing.md : theme.spacing.xl};
`

const ProfileCard = styled.div`
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
`

const Header = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border-radius: ${({ theme }) => theme.borderRadius.lg} ${({ theme }) => theme.borderRadius.lg} 0 0;
  text-align: center;
`

const Logo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`

const LogoIcon = styled.div`
  width: 48px;
  height: 48px;
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  opacity: 0.9;
`

const Title = styled.h1`
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
  font-size: ${({ theme }) => theme.fontSizes.xl};
`

const Subtitle = styled.p`
  margin: 0;
  opacity: 0.9;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const Body = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
`

const SetupRequiredBanner = styled.div`
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  text-align: center;
  color: #856404;
  font-weight: 500;
`

const WelcomeMessage = styled.div`
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  text-align: center;
`

const WelcomeIcon = styled.div`
  font-size: 3rem;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`

const WelcomeText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.fontSizes.md};
`

const FormSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 2px solid ${({ theme }) => theme.colors.secondary};
`

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`

const FormGroupFull = styled(FormGroup)`
  grid-column: 1 / -1;
`

const Label = styled.label`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`

const RequiredStar = styled.span`
  color: ${({ theme }) => theme.colors.danger};
  margin-left: 2px;
`

const Footer = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  border-radius: 0 0 ${({ theme }) => theme.borderRadius.lg} ${({ theme }) => theme.borderRadius.lg};
`

const SubmitButton = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xxl};
`

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: ${({ theme }) => theme.spacing.sm};
`

const SkillItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const SkillName = styled.span`
  flex: 1;
`

const SkillSelect = styled(Select)`
  width: 85px;
  padding: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.fontSizes.xs};
`

const CategoryGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`

const CategoryTitle = styled.h4`
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const LogoutButton = styled.button`
  position: absolute;
  top: ${({ theme }) => theme.spacing.md};
  right: ${({ theme }) => theme.spacing.md};
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`

const SuccessMessage = styled.div`
  background: #4CAF50;
  color: white;
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`

const SuccessIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`

const HeaderWrapper = styled.div`
  position: relative;
`

const ErrorMessage = styled.div`
  background: ${({ theme }) => theme.colors.danger}15;
  color: ${({ theme }) => theme.colors.danger};
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

// Human-readable status labels
const STATUS_LABELS: Record<string, string> = {
    en_curso: 'En Curso',
    ganada: 'Ganada',
    perdida: 'Perdida',
    cancelada: 'Cancelada',
    en_negociacion: 'En Negociación',
    presentada: 'Presentada',
    en_preparacion: 'En Preparación',
    en_revision: 'En Revisión',
    borrador: 'Borrador',
    activo: 'Activo',
    pendiente: 'Pendiente',
    prospecto: 'Prospecto',
    planificacion: 'Planificación',
    finalizado: 'Finalizado',
    completado: 'Completado',
    cerrado: 'Cerrado',
    no_iniciado: 'No Iniciado',
    en_espera: 'En Espera',
    incompleta: 'Incompleta',
}

const formatStatusLabel = (status: string): string => {
    return STATUS_LABELS[status] || status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

// Skill levels
const SKILL_LEVELS = [
    { value: 'nulo', label: '-' },
    { value: 'bajo', label: 'Bajo' },
    { value: 'medio', label: 'Medio' },
    { value: 'alto', label: 'Alto' },
]

interface SkillLevel {
    skillId: string
    categoryId: number  // Database category ID for saving
    category: string
    name: string
    level: string
}

interface Certificate {
    name: string
    issuer?: string
    date_obtained?: string
    expiry_date?: string
    credential_id?: string
    file_path?: string
}

interface Title {
    name: string
    institution?: string
    date_obtained?: string
    file_path?: string
}

interface Categories {
    tecnologias: Record<string, string[]>
    sectores: string[]
}

// Styled components for certificates section
const CertificatesGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`

const CertificateItem = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: flex-start;
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`

const CertificateInputs = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${({ theme }) => theme.spacing.sm};

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`

const RemoveButton = styled.button`
  background: ${({ theme }) => theme.colors.danger};
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSizes.sm};

  &:hover {
    opacity: 0.9;
  }
`

const AddButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  margin-top: ${({ theme }) => theme.spacing.sm};

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const FileUploadSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`

const FileUploadBox = styled.div`
  border: 2px dashed ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.lg};
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.secondary};
  }
`

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`

const FileName = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text};
`

const FileActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`

const SmallButton = styled.button<{ variant?: 'danger' | 'primary' }>`
  background: ${({ theme, variant }) => variant === 'danger' ? theme.colors.danger : theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSizes.xs};

  &:hover {
    opacity: 0.9;
  }
`

// CV autofill: the server reads the CV in the background; the page polls it this often
const CV_READ_POLL_MS = 3000

// CV autofill (Gemini reads the CV and pre-fills the empty fields)
const AutofillPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  border: 1px solid ${({ theme }) => theme.colors.primary}40;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.primary}0d;

  @media (max-width: 500px) {
    flex-direction: column;
    align-items: stretch;
  }
`

const AutofillText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text};
`

const AutofillButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }
`

const AutofillNotice = styled.div`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: #fff8e1;
  color: #8d6e00;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const AiBadge = styled.span`
  display: inline-block;
  margin-left: ${({ theme }) => theme.spacing.xs};
  padding: 0 6px;
  border-radius: 999px;
  background: #fff3c4;
  color: #8d6e00;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 600;
  white-space: nowrap;
  cursor: help;
`

const ItemActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing.xs};
`

const ExperienceInputs = styled(CertificateInputs)`
  grid-template-columns: 1fr 1fr;
`

const CheckboxLabel = styled.label`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text};
`

const DescriptionArea = styled(TextArea)`
  grid-column: 1 / -1;
  min-height: 60px;
`

// Mi Estado (My Status) panel components
const StatusPanel = styled.div`
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`

const StatusHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`

const StatusTitle = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.primary};
`

const LoadIndicator = styled.div<{ load: number }>`
  background: ${({ load }) => {
        if (load === 0) return '#9E9E9E'
        if (load < 50) return '#4CAF50'
        if (load <= 80) return '#FF9800'
        return '#F44336'
    }};
  color: white;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSizes.md};
`

const ProjectsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.md};
`

const ProjectCard = styled.div`
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-left: 4px solid ${({ theme }) => theme.colors.primary};
`

const ProjectInfo = styled.div`
  flex: 1;
`

const ProjectName = styled.div`
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`

const ProjectLink = styled.button`
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    color: ${({ theme }) => theme.colors.primary};
    font: inherit;
    text-align: left;
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }
`

const ProjectMeta = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`

const ProjectLoad = styled.div<{ load: number }>`
  background: ${({ load }) => {
        if (load < 30) return '#E8F5E9'
        if (load <= 50) return '#FFF3E0'
        return '#FFEBEE'
    }};
  color: ${({ load }) => {
        if (load < 30) return '#2E7D32'
        if (load <= 50) return '#EF6C00'
        return '#C62828'
    }};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const NoProjectsMessage = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.textMuted};
`

interface UserOpportunity {
    id: number
    name: string
    estado: string
    sector: string
    role: string
    capacity_pct: number
    assigned_at: string
    team_type?: 'oferta' | 'proyecto'
    fecha_inicio?: string | null
    fecha_fin?: string | null
    /** Backend flag: this row adds to the person's load today under the shared load rules. */
    is_current_load?: boolean
}

export const ProfilePage: React.FC = () => {
    const { user, logout, refreshUser, needsProfileSetup } = useAuth()
    const { userId } = useParams<{ userId: string }>()
    const location = useLocation()
    const navigate = useNavigate()

    // Check if we're inside MainLayout (path like /profile, not standalone)
    const inLayout = location.pathname.startsWith('/') && !location.pathname.includes('standalone')

    // Determine which user to view: URL param or logged-in user
    const targetUserId = userId ? parseInt(userId) : user?.id
    const isViewingOther = userId && user?.id !== parseInt(userId)
    const isAdmin = user?.role?.toLowerCase() === 'admin'
    const isDirector = isAdmin && user?.job_role === 'Director'

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [saved, setSaved] = useState(false)
    const [error, setError] = useState('')
    const [targetUserName, setTargetUserName] = useState('')
    const [jobRoles, setJobRoles] = useState<string[]>([])
    const [profile, setProfile] = useState({
        display_name: '',
        department: 'IA',  // Always IA
        job_role: '',
        years_experience: '',
    })
    const [techSkills, setTechSkills] = useState<SkillLevel[]>([])
    const [sectorSkills, setSectorSkills] = useState<SkillLevel[]>([])
    const [certificates, setCertificates] = useState<Certificate[]>([])
    const [titles, setTitles] = useState<Title[]>([])
    const [experiences, setExperiences] = useState<Experience[]>([])
    const [cvFilename, setCvFilename] = useState<string | null>(null)
    const [uploadingCV, setUploadingCV] = useState(false)
    // CV autofill. The read runs on the server and survives closing the page; its
    // state is polled while queued/running and its proposal filled in once done.
    // What the CV filled stays marked (✨) until edited or saved.
    const [cvExtraction, setCvExtraction] = useState<CvExtractionState | null>(null)
    const cvReading = cvExtraction?.status === 'queued' || cvExtraction?.status === 'running'
    // The read (by started_at) whose proposal is already in the form, so it is filled once
    const filledRead = useRef<string | null>(null)
    // "Rellenar desde mi CV" fills even a proposal that was saved before
    const fillRequested = useRef(false)
    const [autofillNotice, setAutofillNotice] = useState<string | null>(null)
    const [aiFields, setAiFields] = useState<Set<string>>(new Set())
    const [aiSkillEvidence, setAiSkillEvidence] = useState<Record<string, string>>({})
    // Entries the CV added; editing one replaces the object, which drops its mark
    const aiEntries = useRef(new WeakSet<object>())
    const fileInputRef = useState<HTMLInputElement | null>(null)
    // My Status panel state
    const [myOpportunities, setMyOpportunities] = useState<UserOpportunity[]>([])
    const [loadingOpportunities, setLoadingOpportunities] = useState(false)
    const [upcomingMonths, setUpcomingMonths] = useState(3)
    const [loadHistory, setLoadHistory] = useState<{ week_start: string; avg_load_pct: number; is_projected?: boolean }[]>([])
    const [loadHistoryMonths, setLoadHistoryMonths] = useState(6)
    // Read-only summary for non-admins: /oportunidades is admin-only and bounced them to /profile
    const [summaryOppId, setSummaryOppId] = useState<number | null>(null)
    const openOpp = (id: number) => openOpportunity(navigate, id, { isAdmin, onReadOnly: setSummaryOppId })

    useEffect(() => {
        if (targetUserId) {
            loadData()
            // Load opportunities for users viewing their own profile (except Directors)
            if (!isViewingOther && !isDirector) {
                loadMyOpportunities()
            }
        }
    }, [targetUserId, isViewingOther, isAdmin])

    // Reload load history when range changes
    useEffect(() => {
        if (targetUserId && !isViewingOther && !isDirector) {
            const endDate = new Date().toISOString().split('T')[0]
            const startDate = new Date(Date.now() - loadHistoryMonths * 30 * 86400000).toISOString().split('T')[0]
            api.users.getLoadHistory(targetUserId, startDate, endDate)
                .then((res) => setLoadHistory(res.data || []))
                .catch((err) => console.error('Error loading load history:', err))
        }
    }, [loadHistoryMonths])

    const handleLogout = () => {
        logout()
    }

    const loadMyOpportunities = async () => {
        if (!targetUserId) return
        setLoadingOpportunities(true)
        try {
            const endDate = new Date().toISOString().split('T')[0]
            const startDate = new Date(Date.now() - loadHistoryMonths * 30 * 86400000).toISOString().split('T')[0]
            const [oppRes, histRes] = await Promise.all([
                api.users.getOpportunities(targetUserId),
                api.users.getLoadHistory(targetUserId, startDate, endDate),
            ])
            setMyOpportunities(oppRes.data || [])
            setLoadHistory(histRes.data || [])
        } catch (err) {
            console.error('Error loading opportunities:', err)
        } finally {
            setLoadingOpportunities(false)
        }
    }

    const loadData = async () => {
        if (!targetUserId) return
        setLoading(true)
        setSaved(false)
        try {
            const [profileRes, categoriesRes, rolesRes, userCategoriesRes, opportunitiesConfigRes, cvExtractionRes] = await Promise.all([
                api.users.getProfile(targetUserId),
                api.config.getCategories(),
                api.config.getRoles(),
                // Knowledge values of the profile being shown. /categories is scoped to the
                // session user, so /profile/:userId used to show the viewer's own levels.
                isViewingOther
                    ? api.users.getKnowledge(targetUserId)
                    : api.dashboard.getUserCategories(),
                api.config.getOpportunities(),
                // The background read of the own CV: a proposal finished while the user was away is
                // filled in below. Nothing about it (not even a throw) may stop the profile loading.
                isViewingOther
                    ? Promise.resolve({ data: null })
                    : Promise.resolve()
                        .then(() => api.users.getCvExtraction(targetUserId))
                        .catch((err) => {
                            console.error('Error reading the CV read state:', err)
                            return { data: null }
                        }),
            ])

            setUpcomingMonths(opportunitiesConfigRes.data?.profile?.upcoming_months || 3)

            const profileData = profileRes.data
            setTargetUserName(profileData.display_name || profileData.username || '')
            setProfile({
                display_name: profileData.display_name || '',
                department: 'IA',  // Always IA
                job_role: profileData.job_role || '',
                years_experience: profileData.years_experience?.toString() || '',
            })

            // Load roles from config
            setJobRoles(rolesRes.data?.roles || [])

            // Load certificates, titles and experiences
            setCertificates(profileData.certificates || [])
            setTitles(profileData.titles || [])
            setExperiences(profileData.experiences || [])

            // Load CV filename
            setCvFilename(profileData.cv_filename || null)

            // Build a map of category knowledge from user's current values
            // userCategoriesRes.data has: { id, category_group, subcategory_name, value, ... }
            const userCategories = userCategoriesRes.data || []
            const categoryMap = new Map<string, { id: number; value: string }>()
            userCategories.forEach((cat: any) => {
                const key = `${cat.category_group}-${cat.subcategory_name}`
                categoryMap.set(key, { id: cat.id, value: cat.value || 'nulo' })
            })

            const cats: Categories = categoriesRes.data || { tecnologias: {}, sectores: [] }

            // Initialize tech skills with database IDs and current values
            const techs: SkillLevel[] = []
            if (cats.tecnologias && typeof cats.tecnologias === 'object') {
                Object.entries(cats.tecnologias).forEach(([category, items]) => {
                    if (Array.isArray(items)) {
                        items.forEach((item: string) => {
                            const key = `${category}-${item}`
                            const catInfo = categoryMap.get(key)
                            techs.push({
                                skillId: `tech-${category}-${item}`,
                                categoryId: catInfo?.id || 0,
                                category,
                                name: item,
                                level: catInfo?.value || 'nulo',
                            })
                        })
                    }
                })
            }
            setTechSkills(techs)

            // Initialize sector skills with database IDs and current values
            const sectors: SkillLevel[] = (cats.sectores || []).map((sector: string) => {
                const key = `Sectores-${sector}`
                const catInfo = categoryMap.get(key)
                return {
                    skillId: `sector-${sector}`,
                    categoryId: catInfo?.id || 0,
                    category: 'Sectores',
                    name: sector,
                    level: catInfo?.value || 'nulo',
                }
            })
            setSectorSkills(sectors)

            filledRead.current = null
            setCvExtraction(cvExtractionRes.data ?? null)
        } catch (err) {
            console.error('Error loading profile:', err)
            setError('Error al cargar el perfil')
        } finally {
            setLoading(false)
        }
    }

    const handleProfileChange = (field: string, value: string) => {
        setProfile((prev) => ({ ...prev, [field]: value }))
        setAiFields((prev) => {
            if (!prev.has(field)) return prev
            const next = new Set(prev)
            next.delete(field)
            return next
        })
        setError('')
    }

    const dropSkillMark = (skillId: string) => {
        setAiSkillEvidence((prev) => {
            if (!(skillId in prev)) return prev
            const { [skillId]: _dropped, ...rest } = prev
            return rest
        })
    }

    const handleTechSkillChange = (skillId: string, level: string) => {
        setTechSkills((prev) =>
            prev.map((s) => (s.skillId === skillId ? { ...s, level } : s))
        )
        dropSkillMark(skillId)
    }

    const handleSectorSkillChange = (skillId: string, level: string) => {
        setSectorSkills((prev) =>
            prev.map((s) => (s.skillId === skillId ? { ...s, level } : s))
        )
        dropSkillMark(skillId)
    }

    // Experience handlers
    const handleAddExperience = () => {
        if (experiences.length >= MAX_EXPERIENCES) {
            setError(`Máximo ${MAX_EXPERIENCES} experiencias permitidas`)
            return
        }
        setExperiences([...experiences, { company: '', position: '', start_date: '', end_date: '', current: false, description: '' }])
    }

    const handleRemoveExperience = (index: number) => {
        setExperiences(experiences.filter((_, i) => i !== index))
    }

    const handleExperienceChange = (index: number, changes: Partial<Experience>) => {
        setExperiences(experiences.map((exp, i) => {
            if (i !== index) return exp
            const next = { ...exp, ...changes }
            // A current position has no end month
            if (next.current) next.end_date = ''
            return next
        }))
    }

    // Always the latest form: the autofill answer lands 20-40 s after the click,
    // and whatever the user typed meanwhile must be merged, not overwritten.
    const latestForm = useRef({ profile, experiences, certificates, titles, techSkills, sectorSkills, jobRoles })
    latestForm.current = { profile, experiences, certificates, titles, techSkills, sectorSkills, jobRoles }

    const fillFromProposal = (proposal: CvExtraction) => {
        const current = latestForm.current
        const result = applyCvExtraction(
            {
                yearsExperience: current.profile.years_experience,
                jobRole: current.profile.job_role,
                experiences: current.experiences,
                certificates: current.certificates,
                titles: current.titles,
                techSkills: current.techSkills,
                sectorSkills: current.sectorSkills,
            },
            proposal,
            current.jobRoles,
        )
        setProfile((prev) => ({
            ...prev,
            years_experience: result.form.yearsExperience,
            job_role: result.form.jobRole,
        }))
        setExperiences(result.form.experiences)
        setCertificates(result.form.certificates)
        setTitles(result.form.titles)
        setTechSkills(result.form.techSkills)
        setSectorSkills(result.form.sectorSkills)
        result.newEntries.forEach((entry) => aiEntries.current.add(entry))
        setAiFields((prev) => new Set([...prev, ...result.filledFields]))
        setAiSkillEvidence((prev) => ({ ...prev, ...result.skillEvidence }))
        setAutofillNotice(describeAutofill(result.counts))
        setSaved(false)
    }

    // A done read is filled in once: on load (finished while the user was away), when a
    // poll sees it finish, or on "Rellenar desde mi CV". One already saved is not, unless asked.
    useEffect(() => {
        if (loading || isViewingOther || cvExtraction?.status !== 'done' || !cvExtraction.result) return
        const read = cvExtraction.started_at ?? ''
        if (filledRead.current === read) return
        if (cvExtraction.applied_at && !fillRequested.current) return
        fillRequested.current = false
        filledRead.current = read
        fillFromProposal(cvExtraction.result)
    }, [cvExtraction, loading])

    // Poll the read while it is queued or running (it carries on on the server if the page goes away)
    useEffect(() => {
        if (!cvReading || !targetUserId || isViewingOther) return
        const timer = window.setTimeout(async () => {
            try {
                const { data } = await api.users.getCvExtraction(targetUserId)
                setCvExtraction(data ?? null)
            } catch (err) {
                console.error('Error polling the CV read:', err)
                // Keep polling: a new object re-runs this effect
                setCvExtraction((prev) => (prev ? { ...prev } : prev))
            }
        }, CV_READ_POLL_MS)
        return () => window.clearTimeout(timer)
    }, [cvExtraction, targetUserId, isViewingOther])

    const runCvAutofill = async () => {
        if (!targetUserId || isViewingOther) return
        setAutofillNotice(null)
        setError('')
        try {
            // Starts the read, joins the one in progress or returns the finished one (no new Gemini call)
            const { data } = await api.users.extractCV(targetUserId)
            fillRequested.current = true
            filledRead.current = null
            setCvExtraction(data)
        } catch (err: any) {
            console.error('Error starting the CV read:', err)
            const detail = err.response?.data?.detail
            setError(
                typeof detail === 'string'
                    ? `No se pudo rellenar desde el CV: ${detail}`
                    : 'No se pudo rellenar desde el CV. Inténtalo de nuevo o completa el perfil a mano.'
            )
        }
    }

    // Certificate handlers
    const handleAddCertificate = () => {
        if (certificates.length >= 10) {
            setError('Máximo 10 certificados permitidos')
            return
        }
        setCertificates([...certificates, { name: '' }])
    }

    const handleRemoveCertificate = (index: number) => {
        setCertificates(certificates.filter((_, i) => i !== index))
    }

    const handleCertificateChange = (index: number, field: keyof Certificate, value: string) => {
        setCertificates(certificates.map((cert, i) =>
            i === index ? { ...cert, [field]: value } : cert
        ))
    }

    // Title handlers
    const handleAddTitle = () => {
        if (titles.length >= 10) {
            setError('Máximo 10 títulos permitidos')
            return
        }
        setTitles([...titles, { name: '' }])
    }

    const handleRemoveTitle = (index: number) => {
        setTitles(titles.filter((_, i) => i !== index))
    }

    const handleTitleChange = (index: number, field: keyof Title, value: string) => {
        setTitles(titles.map((t, i) =>
            i === index ? { ...t, [field]: value } : t
        ))
    }

    // CV handlers
    const handleCVUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!targetUserId || isViewingOther || !event.target.files || !event.target.files[0]) return

        const file = event.target.files[0]
        const maxSize = 20 * 1024 * 1024

        if (file.size > maxSize) {
            setError('El archivo es demasiado grande. Máximo 20MB.')
            return
        }

        const allowedTypes = [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ]
        if (!allowedTypes.includes(file.type)) {
            setError('Tipo de archivo no permitido. Solo PDF o Word.')
            return
        }

        setUploadingCV(true)
        setError('')

        try {
            const response = await api.users.uploadCV(targetUserId, file)
            setCvFilename(response.data.filename)
            // The server is already reading it: polled from here, kept if the page closes
            filledRead.current = null
            setCvExtraction(response.data.extraction ?? null)
        } catch (err: any) {
            console.error('Error uploading CV:', err)
            setError(err.response?.data?.detail || 'Error al subir el CV')
        } finally {
            setUploadingCV(false)
            // The same file can be picked again after deleting the CV
            event.target.value = ''
        }
    }

    const handleCVDownload = async () => {
        if (!targetUserId) return

        try {
            const response = await api.users.getCV(targetUserId)
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', cvFilename || 'cv.pdf')
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
        } catch (err: any) {
            console.error('Error downloading CV:', err)
            if (err.response?.status === 403) {
                setError('No tienes permisos para descargar este archivo')
            } else if (err.response?.status === 404) {
                setError('El CV no está disponible. Intenta subirlo de nuevo.')
            } else {
                setError('Error al descargar el CV')
            }
        }
    }

    const handleCVDelete = async () => {
        if (!targetUserId || isViewingOther || !window.confirm('¿Estás seguro de eliminar tu CV?')) return

        try {
            await api.users.deleteCV(targetUserId)
            setCvFilename(null)
            setCvExtraction(null)
        } catch (err) {
            console.error('Error deleting CV:', err)
            setError('Error al eliminar el CV')
        }
    }

    // Helper: compute the DB index for a certificate (excluding unnamed ones that aren't saved)
    const getDbCertIndex = (uiIndex: number): number => {
        // Only named certificates are saved to DB, so we count valid ones before this index
        let dbIndex = 0
        for (let i = 0; i < uiIndex; i++) {
            if (certificates[i]?.name?.trim()) dbIndex++
        }
        return dbIndex
    }

    // Certificate file handlers
    const handleCertificateFileUpload = async (index: number, file: File) => {
        if (!targetUserId) return

        // Validate file type
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp']
        if (!allowedTypes.includes(file.type)) {
            setError('Tipo de archivo no permitido. Solo PDF o imágenes (JPEG, PNG, WEBP).')
            return
        }

        // Validate certificate has a name
        const cert = certificates[index]
        if (!cert || !cert.name.trim()) {
            setError('Por favor, introduce un nombre para el certificado antes de subir el archivo.')
            return
        }

        try {
            // First, save the profile to ensure the certificate exists in the database
            const validCertificates = certificates.filter(c => c.name.trim())
            await api.users.updateProfile(targetUserId, {
                display_name: profile.display_name || undefined,
                department: profile.department || undefined,
                job_role: profile.job_role || undefined,
                years_experience: profile.years_experience ? parseInt(profile.years_experience) : undefined,
                certificates: validCertificates,
            })

            // Calculate DB index for this certificate
            const validIndex = validCertificates.findIndex(c => c === cert)
            if (validIndex === -1) {
                setError('Error: No se encontró el certificado.')
                return
            }

            await api.users.uploadCertificateFile(targetUserId, validIndex, file)
            // Reload profile to get the real file_path from the database
            const refreshed = await api.users.getProfile(targetUserId)
            setCertificates(refreshed.data.certificates || [])
            setError('')
        } catch (err: any) {
            console.error('Error uploading certificate file:', err)
            setError(err.response?.data?.detail || 'Error al subir el archivo del certificado. Asegúrate de guardar el perfil primero.')
        }
    }

    const handleCertificateFileDownload = async (index: number, certName: string) => {
        if (!targetUserId) return

        // Use DB-consistent index (only named certificates are stored)
        const dbIndex = getDbCertIndex(index)

        try {
            const response = await api.users.getCertificateFile(targetUserId, dbIndex)
            // Determine extension from response content-type
            const contentType = response.headers?.['content-type'] || ''
            let ext = '.pdf'
            if (contentType.includes('jpeg') || contentType.includes('jpg')) ext = '.jpg'
            else if (contentType.includes('png')) ext = '.png'
            else if (contentType.includes('webp')) ext = '.webp'

            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `${certName.replace(/\s+/g, '_')}_certificado${ext}`)
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
        } catch (err: any) {
            console.error('Error downloading certificate file:', err)
            if (err.response?.status === 404) {
                setError(`El archivo del certificado "${certName}" no está disponible. Intenta subirlo de nuevo.`)
            } else if (err.response?.status === 403) {
                setError('No tienes permisos para descargar este archivo')
            } else {
                setError('Error al descargar el archivo del certificado')
            }
        }
    }

    const handleCertificateFileDelete = async (index: number) => {
        if (!targetUserId || !window.confirm('¿Estás seguro de eliminar el archivo del certificado?')) return

        // Use DB-consistent index
        const dbIndex = getDbCertIndex(index)

        try {
            await api.users.deleteCertificateFile(targetUserId, dbIndex)
            setCertificates(prev => prev.map((cert, i) =>
                i === index ? { ...cert, file_path: undefined } : cert
            ))
        } catch (err) {
            console.error('Error deleting certificate file:', err)
            setError('Error al eliminar el archivo del certificado')
        }
    }

    // Helper: compute the DB index for a title (excluding unnamed ones that aren't saved)
    const getDbTitleIndex = (uiIndex: number): number => {
        let dbIndex = 0
        for (let i = 0; i < uiIndex; i++) {
            if (titles[i]?.name?.trim()) dbIndex++
        }
        return dbIndex
    }

    // Title file handlers
    const handleTitleFileUpload = async (index: number, file: File) => {
        if (!targetUserId) return

        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp']
        if (!allowedTypes.includes(file.type)) {
            setError('Tipo de archivo no permitido. Solo PDF o imágenes (JPEG, PNG, WEBP).')
            return
        }

        const title = titles[index]
        if (!title || !title.name.trim()) {
            setError('Por favor, introduce un nombre para el título antes de subir el archivo.')
            return
        }

        try {
            const validTitles = titles.filter(t => t.name.trim())
            await api.users.updateProfile(targetUserId, {
                display_name: profile.display_name || undefined,
                department: profile.department || undefined,
                job_role: profile.job_role || undefined,
                years_experience: profile.years_experience ? parseInt(profile.years_experience) : undefined,
                titles: validTitles,
            })

            const validIndex = validTitles.findIndex(t => t === title)
            if (validIndex === -1) {
                setError('Error: No se encontró el título.')
                return
            }

            await api.users.uploadTitleFile(targetUserId, validIndex, file)
            const refreshed = await api.users.getProfile(targetUserId)
            setTitles(refreshed.data.titles || [])
            setError('')
        } catch (err: any) {
            console.error('Error uploading title file:', err)
            setError(err.response?.data?.detail || 'Error al subir el archivo del título. Asegúrate de guardar el perfil primero.')
        }
    }

    const handleTitleFileDownload = async (index: number, titleName: string) => {
        if (!targetUserId) return

        const dbIndex = getDbTitleIndex(index)

        try {
            const response = await api.users.getTitleFile(targetUserId, dbIndex)
            const contentType = response.headers?.['content-type'] || ''
            let ext = '.pdf'
            if (contentType.includes('jpeg') || contentType.includes('jpg')) ext = '.jpg'
            else if (contentType.includes('png')) ext = '.png'
            else if (contentType.includes('webp')) ext = '.webp'

            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `${titleName.replace(/\s+/g, '_')}_titulo${ext}`)
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
        } catch (err: any) {
            console.error('Error downloading title file:', err)
            if (err.response?.status === 404) {
                setError(`El archivo del título "${titleName}" no está disponible. Intenta subirlo de nuevo.`)
            } else if (err.response?.status === 403) {
                setError('No tienes permisos para descargar este archivo')
            } else {
                setError('Error al descargar el archivo del título')
            }
        }
    }

    const handleTitleFileDelete = async (index: number) => {
        if (!targetUserId || !window.confirm('¿Estás seguro de eliminar el archivo del título?')) return

        const dbIndex = getDbTitleIndex(index)

        try {
            await api.users.deleteTitleFile(targetUserId, dbIndex)
            setTitles(prev => prev.map((t, i) =>
                i === index ? { ...t, file_path: undefined } : t
            ))
        } catch (err) {
            console.error('Error deleting title file:', err)
            setError('Error al eliminar el archivo del título')
        }
    }

    const handleSubmit = async () => {
        console.log('handleSubmit called', { targetUserId, isViewingOther, profile })

        if (!targetUserId) {
            setError('Error: No se pudo identificar el usuario. Por favor, vuelve a iniciar sesión.')
            return
        }

        if (isViewingOther) {
            setError('No puedes editar el perfil de otro usuario.')
            return
        }

        // Validation
        if (!profile.job_role) {
            setError('Por favor selecciona tu rol/perfil')
            return
        }
        if (!profile.years_experience) {
            setError('Por favor indica tus años de experiencia')
            return
        }

        // Validate certificates and titles have names
        const validCertificates = certificates.filter(c => c.name.trim())
        const validTitles = titles.filter(t => t.name.trim())
        const validExperiences = experiences.filter(e => e.company.trim())
        const badDates = validExperiences.find(e => !isYearMonth(e.start_date) || !isYearMonth(e.end_date))
        if (badDates) {
            setError(`Revisa las fechas de la experiencia en "${badDates.company}" (formato AAAA-MM)`)
            return
        }

        setSaving(true)
        setError('')

        try {
            console.log('Calling API to update profile', targetUserId, {
                display_name: profile.display_name || undefined,
                department: profile.department || undefined,
                job_role: profile.job_role,
                years_experience: parseInt(profile.years_experience),
                certificates: validCertificates,
                titles: validTitles,
                experiences: validExperiences,
            })

            await api.users.updateProfile(targetUserId, {
                display_name: profile.display_name || undefined,
                department: profile.department || undefined,
                job_role: profile.job_role,
                years_experience: parseInt(profile.years_experience),
                certificates: validCertificates,
                titles: validTitles,
                experiences: validExperiences,
            })

            console.log('Profile updated successfully')

            // Save skill levels (tech and sector skills)
            const allSkills = [...techSkills, ...sectorSkills]
            const skillsToSave = allSkills.filter(skill => skill.categoryId > 0)

            console.log('Saving skill levels:', skillsToSave.length)

            // Save each skill level
            const savePromises = skillsToSave.map(skill =>
                api.dashboard.updateCategoryKnowledge(skill.categoryId, skill.level)
            )

            await Promise.all(savePromises)
            console.log('All skill levels saved successfully')

            // The CV proposal went into this save: do not fill it in again on the next visit
            if (cvExtraction?.status === 'done' && !cvExtraction.applied_at && filledRead.current !== null) {
                api.users.markCvExtractionApplied(targetUserId)
                    .then((res) => setCvExtraction(res.data ?? null))
                    .catch((err) => console.error('Error marking the CV proposal as applied:', err))
            }

            // Sync local certificates, titles and experiences to match what was saved (only valid ones)
            setCertificates(validCertificates)
            setTitles(validTitles)
            setExperiences(validExperiences)
            // Saved: the CV suggestions are now the user's data
            aiEntries.current = new WeakSet()
            setAiFields(new Set())
            setAiSkillEvidence({})
            setAutofillNotice(null)

            // Remember if profile setup was needed before refreshing
            const wasSetupRequired = needsProfileSetup

            // Refresh auth context so needsProfileSetup gets recalculated
            await refreshUser()

            // If profile setup was required and is now complete, navigate to home
            if (wasSetupRequired) {
                navigate('/')
                return
            }

            // Show success message
            setSaved(true)
        } catch (err: any) {
            console.error('Error saving profile:', err)
            // A 422 detail is a list of validation errors, not a message
            const detail = err.response?.data?.detail
            setError(typeof detail === 'string' ? detail : 'Error al guardar el perfil. Inténtalo de nuevo.')
        } finally {
            setSaving(false)
        }
    }

    const parseDate = (value?: string | null): Date | null => {
        if (!value) return null
        const d = new Date(value)
        return isNaN(d.getTime()) ? null : d
    }

    // Same active states as the shared load rules (app/api/domain/models/User/load_rules.py);
    // 'pendiente' projects are not running yet.
    const activeStatesByTeam: Record<string, string[]> = {
        proyecto: ['planificacion', 'en_curso'],
        oferta: ['borrador', 'en_preparacion', 'en_revision', 'ganada'],
    }

    const isActiveState = (opp: UserOpportunity): boolean => {
        const teamType = opp.team_type || 'oferta'
        const activeStates = activeStatesByTeam[teamType] || activeStatesByTeam.oferta
        return activeStates.includes((opp.estado || '').toLowerCase())
    }

    // The interns' 'Desarrollo y formación' slot never adds to load
    const isPracticas = (opp: UserOpportunity): boolean =>
        (opp.name || '').trim().toLowerCase() === 'desarrollo y formación'

    // Today's assignments and load come from the backend's is_current_load, the same
    // rules the Timeline, the matrix and Estadísticas use. The page used to apply its
    // own (undated rows counted every day, 'pendiente' projects, the prácticas slot,
    // oferta + proyecto of the same opportunity twice), so e.g. an intern saw 200%.
    const currentAssignments = useMemo(
        () => myOpportunities.filter((opp) => opp.is_current_load === true),
        [myOpportunities]
    )

    // Average of the next 30 days from the load-history projection (same rules, per
    // working day), instead of recomputing it here.
    const weeklyAverageLoad = useMemo(() => {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const horizon = new Date(today)
        horizon.setDate(horizon.getDate() + 30)
        const weeks = loadHistory.filter((w) => {
            if (!w.is_projected) return false
            const start = parseDate(w.week_start)
            return !!start && start <= horizon
        })
        if (weeks.length === 0) return 0
        return Math.round(weeks.reduce((sum, w) => sum + (w.avg_load_pct || 0), 0) / weeks.length)
    }, [loadHistory])

    const upcomingAssignments = useMemo(() => {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const horizon = new Date(today)
        horizon.setMonth(horizon.getMonth() + upcomingMonths)

        return myOpportunities.filter((opp) => {
            if (!isActiveState(opp) || isPracticas(opp)) return false
            const start = parseDate(opp.fecha_inicio)
            if (!start) return false
            return start > today && start <= horizon
        })
    }, [myOpportunities, upcomingMonths])

    // Group tech skills by category
    const techSkillsByCategory = techSkills.reduce((acc, skill) => {
        if (!acc[skill.category]) {
            acc[skill.category] = []
        }
        acc[skill.category].push(skill)
        return acc
    }, {} as Record<string, SkillLevel[]>)

    if (!targetUserId) {
        return null
    }

    return (
        <PageContainer $inLayout={inLayout}>
            <ProfileCard className="animate-fade-in">
                <HeaderWrapper>
                    <Header>
                        {!isViewingOther && !inLayout && (
                            <LogoutButton onClick={handleLogout}>
                                🚪 Cerrar Sesión
                            </LogoutButton>
                        )}
                        <Logo>
                            <LogoIcon />
                            <span style={{ fontSize: '1.2rem', fontWeight: 600 }}>Manager InputKnowledge</span>
                        </Logo>
                        <Title>{isViewingOther ? `👤 Perfil de ${targetUserName}` : '👤 Mi Perfil'}</Title>
                        <Subtitle>
                            {isViewingOther
                                ? 'Información del perfil y CV del usuario'
                                : 'Completa tu información para que el administrador pueda conocer tu experiencia'}
                        </Subtitle>
                    </Header>
                </HeaderWrapper>

                <Body>
                    {loading ? (
                        <LoadingContainer>
                            <Spinner />
                        </LoadingContainer>
                    ) : (
                        <>
                            {saved && !isViewingOther ? (
                                <SuccessMessage>
                                    <SuccessIcon>✅</SuccessIcon>
                                    <strong>¡Perfil guardado correctamente!</strong><br />
                                    El administrador podrá ver tu información actualizada.
                                </SuccessMessage>
                            ) : isViewingOther ? (
                                <WelcomeMessage>
                                    <WelcomeIcon>📋</WelcomeIcon>
                                    <WelcomeText>
                                        Perfil de <strong>{targetUserName}</strong><br />
                                        {cvFilename ? 'Este usuario tiene CV disponible para descargar.' : 'Este usuario aún no ha subido su CV.'}
                                    </WelcomeText>
                                </WelcomeMessage>
                            ) : (
                                <>
                                    {needsProfileSetup && (
                                        <SetupRequiredBanner>
                                            ⚠️ <strong>Configuración obligatoria:</strong> Selecciona tu <strong>Rol / Perfil</strong> y <strong>Años de experiencia</strong>, y pulsa <strong>Guardar Perfil</strong> para acceder al resto de la aplicación.
                                        </SetupRequiredBanner>
                                    )}
                                    <WelcomeMessage>
                                        <WelcomeIcon>👋</WelcomeIcon>
                                        <WelcomeText>
                                            ¡Hola <strong>{user?.display_name || user?.username}</strong>!<br />
                                            Rellena la siguiente información para que el administrador conozca tu experiencia y habilidades.
                                        </WelcomeText>
                                    </WelcomeMessage>
                                </>
                            )}

                            {error && <ErrorMessage>{error}</ErrorMessage>}

                            {!isViewingOther && (
                                <>
                                    <AutofillPanel>
                                        <AutofillText>
                                            <strong>✨ Rellena tu perfil desde el CV</strong>
                                            <span>
                                                {cvFilename
                                                    ? 'La IA lee tu CV (también si es un escaneo) y completa experiencia, estudios, certificados y niveles. Solo rellena lo que está vacío; revísalo antes de guardar.'
                                                    : 'Sube tu CV en PDF o Word y la IA completará experiencia, estudios, certificados y niveles por ti. Revísalo antes de guardar.'}
                                            </span>
                                            {cvExtraction?.status === 'queued' && (
                                                <span style={{ color: '#666' }}>
                                                    ⏳ Hay muchos CVs leyéndose: el tuyo está en cola. Puedes cerrar esta página; lo verás relleno al volver.
                                                </span>
                                            )}
                                            {cvExtraction?.status === 'running' && (
                                                <span style={{ color: '#666' }}>
                                                    ⏳ Leyendo tu CV con IA (suele tardar 20-40 s). Puedes cerrar esta página; lo verás relleno al volver.
                                                </span>
                                            )}
                                            {cvExtraction?.status === 'failed' && cvExtraction.error && (
                                                <span role="alert" style={{ color: '#c62828' }}>⚠️ {cvExtraction.error}</span>
                                            )}
                                        </AutofillText>
                                        <AutofillButton
                                            type="button"
                                            disabled={cvReading || uploadingCV}
                                            onClick={() => (cvFilename ? runCvAutofill() : document.getElementById('cv-upload')?.click())}
                                        >
                                            {cvExtraction?.status === 'queued'
                                                ? '⏳ En cola…'
                                                : cvReading
                                                    ? '⏳ Leyendo CV…'
                                                    : uploadingCV
                                                        ? '⏳ Subiendo…'
                                                        : cvFilename
                                                            ? '✨ Rellenar desde mi CV'
                                                            : '📤 Subir CV y rellenar'}
                                        </AutofillButton>
                                    </AutofillPanel>
                                    {autofillNotice && <AutofillNotice role="status">{autofillNotice}</AutofillNotice>}
                                </>
                            )}

                            {/* My Status Panel - Shown to all users viewing their own profile (except Directors) */}
                            {!isViewingOther && !isDirector && (
                                <>
                                    <StatusPanel>
                                        <StatusHeader>
                                            <StatusTitle>📊 Mi Estado</StatusTitle>
                                            <LoadIndicator load={weeklyAverageLoad}>
                                                {weeklyAverageLoad}% Carga media (próximos 30 días)
                                            </LoadIndicator>
                                        </StatusHeader>
                                        {loadingOpportunities ? (
                                            <LoadingContainer>
                                                <Spinner />
                                            </LoadingContainer>
                                        ) : currentAssignments.length === 0 ? (
                                            <NoProjectsMessage>
                                                <p>🎯 No tienes asignaciones activas a día de hoy</p>
                                                <p style={{ fontSize: '0.8rem' }}>Solo se muestran proyectos/ofertas vigentes hoy.</p>
                                            </NoProjectsMessage>
                                        ) : (
                                            <ProjectsList>
                                                {currentAssignments.map((opp) => (
                                                    <ProjectCard key={`${opp.id}-${opp.team_type || 'oferta'}`}>
                                                        <ProjectInfo>
                                                            <ProjectName>
                                                                <ProjectLink
                                                                    type="button"
                                                                    onClick={() => openOpp(opp.id)}
                                                                >
                                                                    {opp.name}
                                                                </ProjectLink>
                                                            </ProjectName>
                                                            <ProjectMeta>
                                                                {opp.role && <span>🏷️ {opp.role}</span>}
                                                                {opp.team_type && <span> • 🧩 {opp.team_type === 'proyecto' ? 'Proyecto' : 'Oferta'}</span>}
                                                                {opp.sector && <span> • 🏢 {opp.sector}</span>}
                                                                {opp.estado && <span> • 📌 {formatStatusLabel(opp.estado)}</span>}
                                                            </ProjectMeta>
                                                        </ProjectInfo>
                                                        <ProjectLoad load={opp.capacity_pct || 0}>
                                                            {opp.capacity_pct || 0}%
                                                        </ProjectLoad>
                                                    </ProjectCard>
                                                ))}
                                            </ProjectsList>
                                        )}
                                    </StatusPanel>

                                    <StatusPanel>
                                        <StatusHeader>
                                            <StatusTitle>⏭️ Próximo</StatusTitle>
                                            <LoadIndicator load={upcomingAssignments.reduce((sum, opp) => sum + (opp.capacity_pct || 0), 0)}>
                                                {upcomingAssignments.length} asignaciones ({upcomingMonths} meses)
                                            </LoadIndicator>
                                        </StatusHeader>
                                        {loadingOpportunities ? (
                                            <LoadingContainer>
                                                <Spinner />
                                            </LoadingContainer>
                                        ) : upcomingAssignments.length === 0 ? (
                                            <NoProjectsMessage>
                                                <p>🗓️ No tienes asignaciones próximas en los siguientes {upcomingMonths} meses</p>
                                            </NoProjectsMessage>
                                        ) : (
                                            <ProjectsList>
                                                {upcomingAssignments.map((opp) => (
                                                    <ProjectCard key={`upcoming-${opp.id}-${opp.team_type || 'oferta'}`}>
                                                        <ProjectInfo>
                                                            <ProjectName>
                                                                <ProjectLink
                                                                    type="button"
                                                                    onClick={() => openOpp(opp.id)}
                                                                >
                                                                    {opp.name}
                                                                </ProjectLink>
                                                            </ProjectName>
                                                            <ProjectMeta>
                                                                {opp.role && <span>🏷️ {opp.role}</span>}
                                                                {opp.team_type && <span> • 🧩 {opp.team_type === 'proyecto' ? 'Proyecto' : 'Oferta'}</span>}
                                                                {opp.fecha_inicio && <span> • 📅 Inicio {new Date(opp.fecha_inicio).toLocaleDateString('es-ES')}</span>}
                                                            </ProjectMeta>
                                                        </ProjectInfo>
                                                        <ProjectLoad load={opp.capacity_pct || 0}>
                                                            {opp.capacity_pct || 0}%
                                                        </ProjectLoad>
                                                    </ProjectCard>
                                                ))}
                                            </ProjectsList>
                                        )}
                                    </StatusPanel>

                                    {loadHistory.length > 0 && (
                                        <StatusPanel>
                                            <LoadHistoryChart data={loadHistory} onRangeChange={setLoadHistoryMonths} />
                                        </StatusPanel>
                                    )}
                                </>
                            )}

                            <FormSection>
                                <SectionTitle>📋 Información Personal</SectionTitle>
                                <FormGrid>
                                    <FormGroupFull>
                                        <Label>Nombre Completo</Label>
                                        <Input
                                            type="text"
                                            value={profile.display_name}
                                            disabled
                                            readOnly
                                            placeholder="Tu nombre completo"
                                        />
                                    </FormGroupFull>
                                    <FormGroup>
                                        <Label>Departamento</Label>
                                        <Input
                                            type="text"
                                            value="IA"
                                            disabled
                                            readOnly
                                        />
                                    </FormGroup>
                                    <FormGroup>
                                        <Label>
                                            Años de Experiencia<RequiredStar>*</RequiredStar>
                                            {aiFields.has('years_experience') && (
                                                <AiBadge title="Calculado con las fechas de tu CV (los periodos solapados cuentan una vez)">✨ CV</AiBadge>
                                            )}
                                        </Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            max="50"
                                            value={profile.years_experience}
                                            onChange={(e) => handleProfileChange('years_experience', e.target.value)}
                                            placeholder="0"
                                        />
                                    </FormGroup>
                                    <FormGroupFull>
                                        <Label>
                                            Rol / Perfil<RequiredStar>*</RequiredStar>
                                            {aiFields.has('job_role') && (
                                                <AiBadge title="Sugerido por tu puesto más reciente en el CV">✨ CV</AiBadge>
                                            )}
                                        </Label>
                                        <Select
                                            value={profile.job_role}
                                            onChange={(e) => handleProfileChange('job_role', e.target.value)}
                                        >
                                            <option value="">Seleccionar rol...</option>
                                            {jobRoles.map((role) => (
                                                <option key={role} value={role}>
                                                    {role}
                                                </option>
                                            ))}
                                        </Select>
                                    </FormGroupFull>
                                </FormGrid>
                            </FormSection>

                            <FormSection>
                                <SectionTitle>💼 Experiencia Profesional (máx. {MAX_EXPERIENCES})</SectionTitle>
                                <CertificatesGrid>
                                    {experiences.length === 0 && isViewingOther && (
                                        <span style={{ fontSize: '0.8rem', color: '#999' }}>Sin experiencias registradas</span>
                                    )}
                                    {experiences.map((exp, index) => (
                                        <CertificateItem key={index}>
                                            <ExperienceInputs>
                                                <Input
                                                    type="text"
                                                    value={exp.company}
                                                    onChange={(e) => handleExperienceChange(index, { company: e.target.value })}
                                                    placeholder="Empresa *"
                                                    readOnly={Boolean(isViewingOther)}
                                                />
                                                <Input
                                                    type="text"
                                                    value={exp.position || ''}
                                                    onChange={(e) => handleExperienceChange(index, { position: e.target.value })}
                                                    placeholder="Puesto"
                                                    readOnly={Boolean(isViewingOther)}
                                                />
                                                <Input
                                                    type="month"
                                                    value={exp.start_date || ''}
                                                    onChange={(e) => handleExperienceChange(index, { start_date: e.target.value })}
                                                    placeholder="Inicio (AAAA-MM)"
                                                    title="Inicio"
                                                    aria-label="Inicio"
                                                    readOnly={Boolean(isViewingOther)}
                                                />
                                                <Input
                                                    type="month"
                                                    value={exp.current ? '' : exp.end_date || ''}
                                                    onChange={(e) => handleExperienceChange(index, { end_date: e.target.value })}
                                                    placeholder={exp.current ? 'Actualidad' : 'Fin (AAAA-MM)'}
                                                    title="Fin"
                                                    aria-label="Fin"
                                                    disabled={Boolean(exp.current)}
                                                    readOnly={Boolean(isViewingOther)}
                                                />
                                                <CheckboxLabel>
                                                    <input
                                                        type="checkbox"
                                                        checked={Boolean(exp.current)}
                                                        onChange={(e) => handleExperienceChange(index, { current: e.target.checked })}
                                                        disabled={Boolean(isViewingOther)}
                                                    />
                                                    Trabajo aquí actualmente
                                                </CheckboxLabel>
                                                <DescriptionArea
                                                    value={exp.description || ''}
                                                    onChange={(e) => handleExperienceChange(index, { description: e.target.value })}
                                                    placeholder="Descripción breve (opcional)"
                                                    maxLength={1000}
                                                    readOnly={Boolean(isViewingOther)}
                                                />
                                            </ExperienceInputs>
                                            {!isViewingOther && (
                                                <ItemActions>
                                                    <RemoveButton onClick={() => handleRemoveExperience(index)}>
                                                        ✕
                                                    </RemoveButton>
                                                    {aiEntries.current.has(exp) && <AiBadge title="Leído de tu CV">✨ CV</AiBadge>}
                                                </ItemActions>
                                            )}
                                        </CertificateItem>
                                    ))}
                                </CertificatesGrid>
                                {!isViewingOther && (
                                    <AddButton
                                        onClick={handleAddExperience}
                                        disabled={experiences.length >= MAX_EXPERIENCES}
                                    >
                                        + Añadir experiencia
                                    </AddButton>
                                )}
                            </FormSection>

                            <FormSection>
                                <SectionTitle>🛠️ Nivel de Tecnologías</SectionTitle>
                                {Object.entries(techSkillsByCategory).map(([category, skills]) => (
                                    <CategoryGroup key={category}>
                                        <CategoryTitle>{category}</CategoryTitle>
                                        <SkillsGrid>
                                            {skills.map((skill) => (
                                                <SkillItem key={skill.skillId}>
                                                    <SkillName>
                                                        {skill.name}
                                                        {skill.skillId in aiSkillEvidence && (
                                                            <AiBadge title={aiSkillEvidence[skill.skillId] || 'Sugerido por tu CV'}>✨</AiBadge>
                                                        )}
                                                    </SkillName>
                                                    <SkillSelect
                                                        value={skill.level}
                                                        onChange={(e) => handleTechSkillChange(skill.skillId, e.target.value)}
                                                    >
                                                        {SKILL_LEVELS.map((level) => (
                                                            <option key={level.value} value={level.value}>
                                                                {level.label}
                                                            </option>
                                                        ))}
                                                    </SkillSelect>
                                                </SkillItem>
                                            ))}
                                        </SkillsGrid>
                                    </CategoryGroup>
                                ))}
                            </FormSection>

                            <FormSection>
                                <SectionTitle>🏢 Nivel de Sectores</SectionTitle>
                                <SkillsGrid>
                                    {sectorSkills.map((skill) => (
                                        <SkillItem key={skill.skillId}>
                                            <SkillName>
                                                {skill.name}
                                                {skill.skillId in aiSkillEvidence && (
                                                    <AiBadge title={aiSkillEvidence[skill.skillId] || 'Sugerido por tu CV'}>✨</AiBadge>
                                                )}
                                            </SkillName>
                                            <SkillSelect
                                                value={skill.level}
                                                onChange={(e) => handleSectorSkillChange(skill.skillId, e.target.value)}
                                            >
                                                {SKILL_LEVELS.map((level) => (
                                                    <option key={level.value} value={level.value}>
                                                        {level.label}
                                                    </option>
                                                ))}
                                            </SkillSelect>
                                        </SkillItem>
                                    ))}
                                </SkillsGrid>
                            </FormSection>

                            <FormSection>
                                <SectionTitle>🎓 Certificados (máx. 10)</SectionTitle>
                                <CertificatesGrid>
                                    {certificates.map((cert, index) => (
                                        <CertificateItem key={index}>
                                            <CertificateInputs>
                                                <Input
                                                    type="text"
                                                    value={cert.name}
                                                    onChange={(e) => handleCertificateChange(index, 'name', e.target.value)}
                                                    placeholder="Nombre del certificado *"
                                                    readOnly={Boolean(isViewingOther)}
                                                />
                                                <Input
                                                    type="text"
                                                    value={cert.issuer || ''}
                                                    onChange={(e) => handleCertificateChange(index, 'issuer', e.target.value)}
                                                    placeholder="Entidad emisora"
                                                    readOnly={Boolean(isViewingOther)}
                                                />
                                                <Input
                                                    type="date"
                                                    value={cert.date_obtained || ''}
                                                    onChange={(e) => handleCertificateChange(index, 'date_obtained', e.target.value)}
                                                    placeholder="Fecha obtención"
                                                    readOnly={Boolean(isViewingOther)}
                                                />
                                                <Input
                                                    type="date"
                                                    value={cert.expiry_date || ''}
                                                    onChange={(e) => handleCertificateChange(index, 'expiry_date', e.target.value)}
                                                    placeholder="Fecha caducidad"
                                                    readOnly={Boolean(isViewingOther)}
                                                />
                                                {/* Certificate file upload/download */}
                                                <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem' }}>
                                                    {cert.file_path && !cert.file_path.startsWith('uploaded_') ? (
                                                        <>
                                                            <span style={{ fontSize: '0.8rem', color: '#4CAF50' }}>📎 Archivo subido</span>
                                                            {/* Admin or self can download */}
                                                            {(isAdmin || !isViewingOther) && (
                                                                <SmallButton onClick={() => handleCertificateFileDownload(index, cert.name)}>
                                                                    ⬇️ Descargar
                                                                </SmallButton>
                                                            )}
                                                            {/* Only self can delete file */}
                                                            {!isViewingOther && (
                                                                <SmallButton variant="danger" onClick={() => handleCertificateFileDelete(index)}>
                                                                    🗑️
                                                                </SmallButton>
                                                            )}
                                                        </>
                                                    ) : !isViewingOther ? (
                                                        <>
                                                            <input
                                                                type="file"
                                                                id={`cert-file-${index}`}
                                                                accept=".pdf,.jpg,.jpeg,.png,.webp"
                                                                style={{ display: 'none' }}
                                                                onChange={(e) => {
                                                                    const file = e.target.files?.[0]
                                                                    if (file) handleCertificateFileUpload(index, file)
                                                                }}
                                                            />
                                                            <SmallButton
                                                                onClick={() => document.getElementById(`cert-file-${index}`)?.click()}
                                                            >
                                                                📤 Subir archivo
                                                            </SmallButton>
                                                            <span style={{ fontSize: '0.75rem', color: '#666' }}>PDF o imagen</span>
                                                        </>
                                                    ) : (
                                                        <span style={{ fontSize: '0.8rem', color: '#999' }}>Sin archivo</span>
                                                    )}
                                                </div>
                                            </CertificateInputs>
                                            {!isViewingOther && (
                                                <ItemActions>
                                                    <RemoveButton onClick={() => handleRemoveCertificate(index)}>
                                                        ✕
                                                    </RemoveButton>
                                                    {aiEntries.current.has(cert) && <AiBadge title="Leído de tu CV">✨ CV</AiBadge>}
                                                </ItemActions>
                                            )}
                                        </CertificateItem>
                                    ))}
                                </CertificatesGrid>
                                {!isViewingOther && (
                                    <AddButton
                                        onClick={handleAddCertificate}
                                        disabled={certificates.length >= 10}
                                    >
                                        + Añadir certificado
                                    </AddButton>
                                )}
                            </FormSection>

                            <FormSection>
                                <SectionTitle>🏛️ Títulos (máx. 10)</SectionTitle>
                                <CertificatesGrid>
                                    {titles.map((title, index) => (
                                        <CertificateItem key={index}>
                                            <CertificateInputs>
                                                <Input
                                                    type="text"
                                                    value={title.name}
                                                    onChange={(e) => handleTitleChange(index, 'name', e.target.value)}
                                                    placeholder="Nombre del título *"
                                                    readOnly={Boolean(isViewingOther)}
                                                />
                                                <Input
                                                    type="text"
                                                    value={title.institution || ''}
                                                    onChange={(e) => handleTitleChange(index, 'institution', e.target.value)}
                                                    placeholder="Institución"
                                                    readOnly={Boolean(isViewingOther)}
                                                />
                                                <Input
                                                    type="date"
                                                    value={title.date_obtained || ''}
                                                    onChange={(e) => handleTitleChange(index, 'date_obtained', e.target.value)}
                                                    placeholder="Fecha obtención"
                                                    readOnly={Boolean(isViewingOther)}
                                                />
                                                {/* Title file upload/download */}
                                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem' }}>
                                                    {title.file_path && !title.file_path.startsWith('uploaded_') ? (
                                                        <>
                                                            <span style={{ fontSize: '0.8rem', color: '#4CAF50' }}>📎 Archivo subido</span>
                                                            {(isAdmin || !isViewingOther) && (
                                                                <SmallButton onClick={() => handleTitleFileDownload(index, title.name)}>
                                                                    ⬇️ Descargar
                                                                </SmallButton>
                                                            )}
                                                            {!isViewingOther && (
                                                                <SmallButton variant="danger" onClick={() => handleTitleFileDelete(index)}>
                                                                    🗑️
                                                                </SmallButton>
                                                            )}
                                                        </>
                                                    ) : !isViewingOther ? (
                                                        <>
                                                            <input
                                                                type="file"
                                                                id={`title-file-${index}`}
                                                                accept=".pdf,.jpg,.jpeg,.png,.webp"
                                                                style={{ display: 'none' }}
                                                                onChange={(e) => {
                                                                    const file = e.target.files?.[0]
                                                                    if (file) handleTitleFileUpload(index, file)
                                                                }}
                                                            />
                                                            <SmallButton
                                                                onClick={() => document.getElementById(`title-file-${index}`)?.click()}
                                                            >
                                                                📤 Subir archivo
                                                            </SmallButton>
                                                            <span style={{ fontSize: '0.75rem', color: '#666' }}>PDF o imagen</span>
                                                        </>
                                                    ) : (
                                                        <span style={{ fontSize: '0.8rem', color: '#999' }}>Sin archivo</span>
                                                    )}
                                                </div>
                                            </CertificateInputs>
                                            {!isViewingOther && (
                                                <ItemActions>
                                                    <RemoveButton onClick={() => handleRemoveTitle(index)}>
                                                        ✕
                                                    </RemoveButton>
                                                    {aiEntries.current.has(title) && <AiBadge title="Leído de tu CV">✨ CV</AiBadge>}
                                                </ItemActions>
                                            )}
                                        </CertificateItem>
                                    ))}
                                </CertificatesGrid>
                                {!isViewingOther && (
                                    <AddButton
                                        onClick={handleAddTitle}
                                        disabled={titles.length >= 10}
                                    >
                                        + Añadir título
                                    </AddButton>
                                )}
                            </FormSection>

                            <FormSection>
                                <SectionTitle>📄 Curriculum Vitae</SectionTitle>
                                <FileUploadSection>
                                    {cvFilename ? (
                                        <FileInfo>
                                            <FileName>📎 {cvFilename}</FileName>
                                            <FileActions>
                                                {/* Only admin or the user themselves can download CV */}
                                                {(isAdmin || !isViewingOther) && (
                                                    <SmallButton onClick={handleCVDownload}>
                                                        ⬇️ Descargar
                                                    </SmallButton>
                                                )}
                                                {!isViewingOther && (
                                                    <SmallButton variant="danger" onClick={handleCVDelete}>
                                                        🗑️ Eliminar
                                                    </SmallButton>
                                                )}
                                            </FileActions>
                                        </FileInfo>
                                    ) : isViewingOther ? (
                                        <FileUploadBox style={{ cursor: 'default', opacity: 0.6 }}>
                                            <p>📋 Este usuario no ha subido su CV</p>
                                        </FileUploadBox>
                                    ) : (
                                        <>
                                            <input
                                                type="file"
                                                id="cv-upload"
                                                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                                onChange={handleCVUpload}
                                                style={{ display: 'none' }}
                                            />
                                            <FileUploadBox
                                                onClick={() => document.getElementById('cv-upload')?.click()}
                                            >
                                                {uploadingCV ? (
                                                    <p>⏳ Subiendo...</p>
                                                ) : (
                                                    <>
                                                        <p>📤 Haz clic para subir tu CV</p>
                                                        <p style={{ fontSize: '0.8rem', color: '#666' }}>
                                                            PDF o Word (máx. 20MB). Al subirlo, la IA rellenará tu perfil.
                                                        </p>
                                                    </>
                                                )}
                                            </FileUploadBox>
                                        </>
                                    )}
                                </FileUploadSection>
                            </FormSection>

                            {/* Imputación summary (own profile only): the full month lives in /imputacion */}
                            {!isViewingOther && <ImputacionLinkCard />}
                        </>
                    )}
                </Body>

                {!isViewingOther && (
                    <Footer>
                        <SubmitButton onClick={handleSubmit} disabled={saving || loading}>
                            {saving ? 'Guardando...' : '💾 Guardar Perfil'}
                        </SubmitButton>
                    </Footer>
                )}
            </ProfileCard>
            {summaryOppId && (
                <OpportunitySummaryModal
                    opportunityId={summaryOppId}
                    onClose={() => setSummaryOppId(null)}
                    isAdmin={isAdmin}
                />
            )}
        </PageContainer>
    )
}
